import { Card, CardActionArea, CardMedia, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { useMemo } from 'react'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquareSliders } from '@fortawesome/pro-regular-svg-icons'
import { BoxV2 } from '@labrav/react-components'
import { CRMConfiguration } from '../CRMConfiguration/CRMConfiguration'
import { useStyles } from './CRMSelector.styles'
import { ImageMapper } from './CRMSelector'
import { CRM } from '../../modules/crmSelector/action'
import { CRMTypes, themeType } from '../../utils/constants'
import { HelpDrawer } from '../HelpDrawerV2/HelpDrawer'
import { isLoading as loading } from '../../utils/loadingState'
import { LoadingTypes } from '../../modules/loading/reducer'
import { PartnerType } from '../../types/partner'
import { useCrmAuthInfo } from '../../utils/hooks/useCrmAuthInfo'
import { SalesforceIntegrationTypeCard } from './SalesforceIntegrationTypeCard'
import type { CrmOnboardingRootState } from '../../modules/crmSelector/crmOnboardingState'

export type selectedCRM = undefined | CRM | string

type Props = {
  imageMapper: ImageMapper[]
  selectedCRM?: selectedCRM
  crmId: null | string
  isAdmin: boolean
  theme?: themeType
  helpTitle?: string
}

const getSelectedCrm = (selected: selectedCRM): string | undefined => {
  if (!selected) {
    return undefined
  }
  if (typeof selected === 'string') {
    return selected
  }
  return selected.crm_type
}

export const CRMSelectorUI: React.FC<Props> = ({
  imageMapper,
  selectedCRM,
  crmId,
  isAdmin,
  theme,
  helpTitle,
}) => {
  const { classes } = useStyles()
  const isLoading = useSelector((state: CrmOnboardingRootState) =>
    loading(state.loading)(LoadingTypes.GENERAL)
  )
  const { isCrmSelected } = useCrmAuthInfo(crmId)
  const partnerCrmType = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.[
        PartnerType.Admin
      ]?.partnerData?.crmType?.toLowerCase() as CRMTypes | undefined
  )
  const newPlatformSync = useSelector(
    (state: CrmOnboardingRootState) =>
      state.CRMAuth.crmFormData?.crm_schema?.new_platform_sync ?? false
  )

  const isSalesforceCrm =
    getSelectedCrm(selectedCRM) === CRMTypes.salesforce ||
    partnerCrmType === CRMTypes.salesforce

  const showSalesforceIntegrationTypeCard = isAdmin && isSalesforceCrm
  //For returning an image list
  const listCRM = imageMapper.map(crm => (
    <Card
      key={crm.crm_type}
      className={clsx(classes.cardRoot, {
        [classes.selected]: getSelectedCrm(selectedCRM) === crm.crm_type,
        [classes.disable]:
          selectedCRM && getSelectedCrm(selectedCRM) !== crm.crm_type,
      })}
      data-testid="image"
    >
      <CardActionArea
        disabled={crm.disabled || isLoading || !isEmpty(crmId)}
        onClick={crm?.handleClick}
        data-testid={`button-${crm.crm_type}`}
      >
        <CardMedia
          component="img"
          height="91"
          image={crm.logo}
          title={crm.crm_type}
          data-testid={`image-${crm.crm_type}`}
        />
      </CardActionArea>
    </Card>
  ))

  const helpContent = useMemo(() => {
    const crmType = selectedCRM as CRMTypes
    if (crmType === CRMTypes.salesforce) {
      return newPlatformSync
        ? 'crmSettingsSalesforceCompanyRoles'
        : 'crmSettingsSalesforce'
    }
    return (
      {
        [CRMTypes.hubspot]: 'crmSettingsHubspot',
        [CRMTypes.connectwise]: 'crmSettingsConnectwise',
        [CRMTypes.labra]: '',
      }[crmType] || 'crmSettingsGeneralFAQ'
    )
  }, [selectedCRM, newPlatformSync])
  return (
    <div className={classes.selectCrmContainer}>
      <BoxV2
        heading="Connect your CRM"
        style={{
          borderRadius: '0rem 0rem 0.5rem 0.5rem',
        }}
        headingIcon={
          <FontAwesomeIcon
            icon={faSquareSliders}
            style={{ height: '20px', width: '13px' }}
          />
        }
        secondaryActions={
          <HelpDrawer sectionName={helpContent} headerTitle="Help" />
        }
      >
        <Typography className={classes.crmSubTitle}>
          Select a CRM to proceed
        </Typography>
        <div className={classes.imageList}>{listCRM}</div>
      </BoxV2>
      {showSalesforceIntegrationTypeCard && (
        <div style={{ marginTop: '32px' }}>
          <SalesforceIntegrationTypeCard selectedCRM={selectedCRM} />
        </div>
      )}
      <div style={{ marginTop: '32px' }} id="crmForm">
        {isAdmin && !isCrmSelected
          ? null
          : selectedCRM != null && (
              <CRMConfiguration crm={selectedCRM} isAdmin={isAdmin} />
            )}
      </div>
    </div>
  )
}

export default CRMSelectorUI
